import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, articles } from '@/mocks/articlesData';
import type { Article } from '@/mocks/articlesData';

const articleImages: Record<string, string> = {
  'art-001': 'https://readdy.ai/api/search-image?query=A%20warm%20intimate%20scene%20of%20a%20couple%20sharing%20a%20quiet%20evening%20moment%20together%20on%20a%20comfortable%20sofa%2C%20soft%20warm%20lighting%20from%20a%20table%20lamp%2C%20cozy%20living%20room%20atmosphere%2C%20emotional%20connection%2C%20gentle%20and%20tender%20mood%2C%20editorial%20lifestyle%20photography%20with%20soft%20focus&width=1400&height=600&seq=art-hero-001&orientation=landscape',
  'art-002': 'https://readdy.ai/api/search-image?query=An%20elegant%20minimalist%20living%20space%20with%20warm%20morning%20sunlight%20streaming%20through%20large%20windows%2C%20a%20beautifully%20set%20breakfast%20table%20with%20fresh%20flowers%20and%20ceramic%20tableware%2C%20quality%20lifestyle%20aesthetic%2C%20clean%20and%20serene%20atmosphere&width=1400&height=600&seq=art-hero-002&orientation=landscape',
  'art-003': 'https://readdy.ai/api/search-image?query=A%20serene%20person%20sitting%20alone%20by%20a%20window%20with%20warm%20afternoon%20light%2C%20holding%20a%20cup%20of%20tea%2C%20peaceful%20and%20contemplative%20expression%2C%20soft%20natural%20lighting%2C%20minimalist%20interior%20with%20a%20small%20plant&width=1400&height=600&seq=art-hero-003&orientation=landscape',
  'art-004': 'https://readdy.ai/api/search-image?query=Two%20people%20having%20a%20warm%20heartfelt%20conversation%20in%20a%20cozy%20cafe%2C%20soft%20window%20light%2C%20intimate%20atmosphere%2C%20genuine%20emotional%20connection%2C%20warm%20tones%20of%20cream%20and%20gold&width=1400&height=600&seq=art-hero-004&orientation=landscape',
  'art-005': 'https://readdy.ai/api/search-image?query=A%20peaceful%20meditation%20space%20with%20morning%20sunlight%2C%20a%20simple%20cushion%20on%20wooden%20floor%2C%20a%20small%20candle%20and%20dried%20flowers%2C%20minimalist%20zen%20atmosphere%2C%20soft%20natural%20lighting&width=1400&height=600&seq=art-hero-005&orientation=landscape',
  'art-006': 'https://readdy.ai/api/search-image?query=A%20cozy%20warm%20living%20room%20interior%20with%20soft%20throw%20blankets%2C%20flickering%20candles%2C%20indoor%20plants%2C%20warm%20ambient%20lighting%2C%20books%20on%20wooden%20shelves%2C%20inviting%20and%20comfortable%20home%20atmosphere&width=1400&height=600&seq=art-hero-006&orientation=landscape',
  'art-007': 'https://readdy.ai/api/search-image?query=An%20elderly%20couple%20walking%20hand%20in%20hand%20on%20a%20tree%20lined%20path%20during%20golden%20hour%2C%20warm%20sunset%20light%2C%20timeless%20love%20and%20commitment%2C%20emotional%20and%20heartwarming%20scene&width=1400&height=600&seq=art-hero-007&orientation=landscape',
  'art-008': 'https://readdy.ai/api/search-image?query=A%20person%20journaling%20by%20a%20window%20with%20soft%20natural%20light%2C%20a%20cup%20of%20tea%20nearby%2C%20peaceful%20self%20reflection%20moment%2C%20minimalist%20desk%20setup%2C%20calm%20and%20mindful%20atmosphere&width=1400&height=600&seq=art-hero-008&orientation=landscape',
};

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!id) return;
    const found = getArticleById(id);
    if (found) {
      setArticle(found);
      const related = articles
        .filter((a) => a.id !== id && a.category === found.category)
        .slice(0, 3);
      setRelatedArticles(related);
    } else {
      setArticle(null);
    }
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要找的文章可能已被移除或链接有误</p>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回文章列表
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: articleImages[article.id],
    publisher: {
      '@type': 'Organization',
      name: '久久伊人',
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '精选文章', path: '/articles' },
    { label: article.categoryLabel, path: `/articles?category=${article.category}` },
    { label: article.title },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />

        <div className="pt-20">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full py-6 md:py-10">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={articleImages[article.id] || articleImages['art-001']}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex items-center flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                {article.categoryLabel}
              </span>
              {article.tags.map((tag: string) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {article.readTime}
              </span>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 font-medium whitespace-nowrap cursor-pointer hover:text-primary-700 transition-colors duration-200"
                >
                  <i className="ri-arrow-left-line" />
                  返回文章列表
                </Link>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related: Article) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={articleImages[related.id] || articleImages['art-001']}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{related.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}